/**
 * LuxLoyaltyRewardsService — API client for Société rewards.
 *
 * Covers the svc-lux-loyalty rewards endpoints (catalogs, catalog entries, inventory,
 * reservations, flash-hotel pools and config) used by the Classic Rewards and Flash Rewards admin
 * pages, plus the svc-offer endpoints for each offer package's Société reward (see the last section).
 */
import type { actions, paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import { json_headers, request } from '../common';

import { BASE_PATH } from './constants';

// ---------------------------------------------------------------------------
// Response type helpers — extracted from the contract paths
// ---------------------------------------------------------------------------

/** Response type for listing catalogs. */
type ListCatalogsResponse =
  paths['/api/lux-loyalty/rewards/catalogs']['get']['responses']['200']['content']['application/json'];

/** Response type for creating a catalog. */
type CreateCatalogResponse =
  paths['/api/lux-loyalty/rewards/catalogs']['post']['responses']['201']['content']['application/json'];

/** Request body for creating a catalog. */
type CreateCatalogPayload = actions['createRewardsCatalog']['requestBody']['content']['application/json'];

/** Response type for updating a catalog status. */
type UpdateCatalogStatusResponse =
  paths['/api/lux-loyalty/rewards/catalogs/{catalogId}']['patch']['responses']['200']['content']['application/json'];

/** Request body for updating a catalog status. */
type UpdateCatalogStatusPayload = actions['updateRewardsCatalogStatus']['requestBody']['content']['application/json'];

/** Response type for querying entries. */
type QueryEntriesResponse = actions['queryRewardsEntries']['responses']['200']['content']['application/json'];

/** Request body for querying entries. */
type QueryEntriesPayload = actions['queryRewardsEntries']['requestBody']['content']['application/json'];

/** Response type for bulk upserting entries. */
type BulkUpsertEntriesResponse = actions['bulkUpsertRewardsEntries']['responses']['200']['content']['application/json'];

/** Request body for bulk upserting entries. */
type BulkUpsertEntriesPayload = actions['bulkUpsertRewardsEntries']['requestBody']['content']['application/json'];

/** Response type for updating a single entry. */
type UpdateEntryResponse = actions['updateRewardsEntry']['responses']['200']['content']['application/json'];

/** Request body for updating a single entry. */
type UpdateEntryPayload = actions['updateRewardsEntry']['requestBody']['content']['application/json'];

/** Response type for querying inventory. */
type QueryInventoryResponse = actions['queryRewardsInventory']['responses']['200']['content']['application/json'];

/** Response type for bulk upserting inventory. */
type BulkUpsertInventoryResponse =
  actions['bulkUpsertRewardsInventory']['responses']['200']['content']['application/json'];

/** Request body for bulk upserting inventory. */
type BulkUpsertInventoryPayload = actions['bulkUpsertRewardsInventory']['requestBody']['content']['application/json'];

// ---------------------------------------------------------------------------
// Exported entity types — derived from API responses for use in components
// ---------------------------------------------------------------------------

/** A rewards catalog record. */
export type RewardsCatalog = ListCatalogsResponse['result'][number];

/**
 * A catalog entry with pricing.
 *
 * Note: `cashPrice` comes from the API as a string (Prisma Decimal serialization).
 * We normalize it to a number in the service functions below.
 */
export type RawRewardsCatalogEntry = QueryEntriesResponse['result'][number];

/** Normalized catalog entry — cashPrice as number. */
export type RewardsCatalogEntry = Omit<RawRewardsCatalogEntry, 'cashPrice'> & {
  cashPrice: number;
};

type QueryRewardsInventoryRow = QueryInventoryResponse['result'][number];
type BulkUpsertInventoryTierPayload = BulkUpsertInventoryPayload['tiers'][number];

/** The admin flow adds a synthetic `total` row for aggregate redemption caps. */
export type RewardsTier = QueryRewardsInventoryRow['tier'] | 'total';

/** Inventory row per tier for a catalog entry, including the synthetic total row. */
export type RewardsInventoryRow = Omit<QueryRewardsInventoryRow, 'tier'> & {
  tier: RewardsTier;
};

/** Bulk upsert payload for inventory rows, including the synthetic total row. */
export type BulkUpsertRewardsInventoryPayload = Omit<BulkUpsertInventoryPayload, 'tiers'> & {
  tiers: Array<Omit<BulkUpsertInventoryTierPayload, 'tier'> & { tier: RewardsTier }>;
};

/** Catalog entry status. */
export type RewardsCatalogEntryStatus = RawRewardsCatalogEntry['status'];

/** Catalog status. */
export type RewardsCatalogStatus = RewardsCatalog['status'];

// ---------------------------------------------------------------------------
// Normalizer — converts Prisma Decimal strings to numbers
// ---------------------------------------------------------------------------

/** Converts cashPrice from Decimal string to number. */
function normalizeEntry(raw: RawRewardsCatalogEntry): RewardsCatalogEntry {
  return {
    ...raw,
    cashPrice: Number(raw.cashPrice),
  };
}

// ---------------------------------------------------------------------------
// Catalog APIs
// ---------------------------------------------------------------------------

/** Fetches reward catalogs, optionally filtered by itemType and status. */
export async function fetchCatalogs(filters?: {
  itemType?: string;
  status?: RewardsCatalogStatus;
}): Promise<Array<RewardsCatalog>> {
  const params = new URLSearchParams();
  if (filters?.itemType) {
    params.set('itemType', filters.itemType);
  }
  if (filters?.status) {
    params.set('status', filters.status);
  }

  const qs = params.toString();
  const url = `${BASE_PATH}/rewards/catalogs${qs ? `?${qs}` : ''}`;
  const response: ListCatalogsResponse = await request(url, { method: 'GET' });
  return response.result;
}

/** Creates a new rewards catalog. */
export async function createCatalog(data: CreateCatalogPayload): Promise<CreateCatalogResponse['result']> {
  const url = `${BASE_PATH}/rewards/catalogs`;
  const response: CreateCatalogResponse = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result;
}

/** Updates a catalog's status (ACTIVE, INACTIVE, ARCHIVED). */
export async function updateCatalogStatus(
  catalogId: string,
  data: UpdateCatalogStatusPayload,
): Promise<UpdateCatalogStatusResponse['result']> {
  const url = `${BASE_PATH}/rewards/catalogs/${catalogId}`;
  const response: UpdateCatalogStatusResponse = await request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result;
}

// ---------------------------------------------------------------------------
// Entry APIs
// ---------------------------------------------------------------------------

/** Queries catalog entries by catalogId, itemType, itemSubType, and optional itemReferences. */
export async function queryEntries(
  filters: QueryEntriesPayload,
  options?: { signal?: AbortSignal },
): Promise<Array<RewardsCatalogEntry>> {
  const url = `${BASE_PATH}/rewards/catalog-entries/query`;
  const response: QueryEntriesResponse = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(filters),
    signal: options?.signal,
  });
  return response.result.map(normalizeEntry);
}

/** Bulk upserts catalog entries (create or update by unique key). */
export async function bulkUpsertEntries(
  payload: BulkUpsertEntriesPayload,
): Promise<BulkUpsertEntriesResponse['result']> {
  const url = `${BASE_PATH}/rewards/catalog-entries/bulk-upsert`;
  const response: BulkUpsertEntriesResponse = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
  return response.result;
}

/** Updates a single catalog entry's pricing or status. */
export async function updateEntry(entryId: string, data: UpdateEntryPayload): Promise<RewardsCatalogEntry> {
  const url = `${BASE_PATH}/rewards/catalog-entries/${entryId}`;
  const response: UpdateEntryResponse = await request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return normalizeEntry(response.result);
}

// ---------------------------------------------------------------------------
// Inventory APIs
// ---------------------------------------------------------------------------

/** Queries inventory rows by catalogEntryId, catalogId, and/or tier. */
export async function queryInventory(filters: {
  catalogEntryId?: string;
  catalogId?: string;
  tier?: string;
}): Promise<Array<RewardsInventoryRow>> {
  const params = new URLSearchParams();
  if (filters.catalogEntryId) {
    params.set('catalogEntryId', filters.catalogEntryId);
  }
  if (filters.catalogId) {
    params.set('catalogId', filters.catalogId);
  }
  if (filters.tier) {
    params.set('tier', filters.tier);
  }

  const qs = params.toString();
  const url = `${BASE_PATH}/rewards/inventory${qs ? `?${qs}` : ''}`;
  const response: QueryInventoryResponse = await request(url, { method: 'GET' });
  return response.result;
}

/** Bulk upserts inventory tiers for a catalog entry. */
export async function bulkUpsertInventory(
  payload: BulkUpsertRewardsInventoryPayload,
): Promise<BulkUpsertInventoryResponse['result']> {
  const url = `${BASE_PATH}/rewards/inventory/bulk-upsert`;
  const response: BulkUpsertInventoryResponse = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
  return response.result;
}

// ---------------------------------------------------------------------------
// Reservations — admin read endpoints
// Types are derived from the contract (`@luxuryescapes/contract-svc-lux-loyalty`).
// ---------------------------------------------------------------------------

/** Response shape for the admin search endpoint. */
type SearchRewardReservationsResponse =
  actions['searchRewardReservations']['responses']['200']['content']['application/json'];

/** Response shape for the per-order endpoint. */
type GetOrderReservationsResponse = actions['getOrderReservations']['responses']['200']['content']['application/json'];

/** Query parameters accepted by the admin search endpoint. */
type SearchRewardReservationsQuery = actions['searchRewardReservations']['parameters']['query'];

/** A single reservation row as returned by the admin read endpoints. */
export type RewardsReservation = SearchRewardReservationsResponse['result']['reservations'][number];

/** Optional inventory bucket nested under a reservation when includeInventory=true. */
export type RewardsReservationInventory = NonNullable<RewardsReservation['inventory']>;

/** Reservation lifecycle status — narrowed from the contract enum. */
export type RewardsReservationStatus = RewardsReservation['status'];

/** Sort options for the admin search endpoint. */
export type RewardsReservationsSort = NonNullable<SearchRewardReservationsQuery['sort']>;

/**
 * Filter/pagination params for the admin search endpoint.
 *
 * The contract types pagination/sort/includeInventory as required (the
 * generated query schema marks them so because the server applies defaults),
 * but the client treats them as optional — we only serialize values the caller
 * explicitly provides, and the server falls back to its defaults otherwise.
 */
export type SearchRewardsReservationsParams = Partial<SearchRewardReservationsQuery> & {
  /**
   * Catalog-entry reference filters, named after loyalty's columns rather than a product. Hotels
   * hold the offer id in `itemReference` and the package id in `itemSubReference`; flights hold
   * the journey key and ''. `poolId` is the allocation row a hotel reservation consumed.
   * TODO(LOYAL-2863): drop these once the published contract includes them (loyalty 94cbab71).
   */
  itemReference?: string;
  itemSubReference?: string;
  poolId?: string;
};

/** Response payload for the admin search endpoint. */
export type SearchRewardsReservationsResult = SearchRewardReservationsResponse['result'];

/**
 * Admin search across reservations with optional filters and pagination.
 * Returns the most recent reservations first by default (createdAt desc).
 */
export async function searchRewardsReservations(
  params: SearchRewardsReservationsParams,
  options?: { signal?: AbortSignal },
): Promise<SearchRewardsReservationsResult> {
  const qs = new URLSearchParams();
  if (params.orderId) qs.set('orderId', params.orderId);
  if (params.inventoryId) qs.set('inventoryId', params.inventoryId);
  if (params.catalogEntryId) qs.set('catalogEntryId', params.catalogEntryId);
  if (params.catalogId) qs.set('catalogId', params.catalogId);
  if (params.status) qs.set('status', params.status);
  if (params.itemReference) qs.set('itemReference', params.itemReference);
  if (params.itemSubReference) qs.set('itemSubReference', params.itemSubReference);
  if (params.poolId) qs.set('poolId', params.poolId);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.sort) qs.set('sort', params.sort);
  if (params.direction) qs.set('direction', params.direction);
  if (params.includeInventory) qs.set('includeInventory', 'true');

  const search = qs.toString();
  const url = `${BASE_PATH}/rewards/reservations${search ? `?${search}` : ''}`;
  const response: SearchRewardReservationsResponse = await request(url, {
    method: 'GET',
    signal: options?.signal,
  });
  return response.result;
}

/** Lists every classic-reward reservation for one order (oldest first). */
export async function getOrderReservations(
  orderId: string,
  options?: { signal?: AbortSignal; includeInventory?: boolean },
): Promise<GetOrderReservationsResponse['result']['reservations']> {
  const qs = options?.includeInventory ? '?includeInventory=true' : '';
  const url = `${BASE_PATH}/rewards/orders/${orderId}/reservations${qs}`;
  const response: GetOrderReservationsResponse = await request(url, {
    method: 'GET',
    signal: options?.signal,
  });
  return response.result.reservations;
}

// ---------------------------------------------------------------------------
// Flash hotel pool & config
// ---------------------------------------------------------------------------

/**
 * Represents an offer's Société flash-hotel allocation state.
 * Virgin offers inherit the catalog default capacity.
 */
export interface RewardsPoolView {
  offerId: string;
  poolId: string | null;
  totalQuantity: number | null;
  reservedCount: number;
  redeemedCount: number;
  remaining: number;
  version: number;
}

/**
 * The flashHotel catalog's 9 pricing and display constants.
 * Mirrors loyalty's `src/lib/rewards/flashHotelConfig.ts` — keep in sync with that file.
 */
export type FlashHotelCatalogConfig = {
  copayPercentage: number;
  pointsCoveragePercentage: number;
  /** Divides burn points: `points = burn / burnRateAmplifier`. */
  burnRateAmplifier: number;
  minMarginPercent: number;
  pointsRounding: number;
  /** Minimum acceptable value-per-point, in cents. Must not exceed `vppCeilingCents`. */
  vppFloorCents: number;
  /** Maximum acceptable value-per-point, in cents. Must be >= `vppFloorCents`. */
  vppCeilingCents: number;
  defaultAllocation: number;
  minRoomsRemaining: number;
};

/**
 * Reads an offer's Société flash-hotel allocation state (virgin offer = catalog default capacity).
 * TODO(LOYAL-2863): replace local types with @luxuryescapes/contract-svc-lux-loyalty once the version with pool/config endpoints ships.
 */
export async function getRewardsPool(offerId: string): Promise<RewardsPoolView> {
  const response = await request(`${BASE_PATH}/rewards/pools/${offerId}`, { method: 'GET' });
  return response.result;
}

/**
 * Sets (int ≥ 0) or clears (null → catalog default) an offer's allocation cap; returns the fresh pool view.
 * TODO(LOYAL-2863): replace local types with @luxuryescapes/contract-svc-lux-loyalty once the version with pool/config endpoints ships.
 */
export async function upsertRewardsPoolCap(offerId: string, totalQuantity: number | null): Promise<RewardsPoolView> {
  const response = await request(`${BASE_PATH}/rewards/pools/${offerId}/cap`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ totalQuantity }),
  });
  return response.result;
}

/**
 * One page of the admin Société flash-hotel pool listing, newest-updated first.
 */
export interface RewardsPoolsListResult {
  pools: Array<RewardsPoolView & { updatedAt: string }>;
  total: number;
}

/**
 * Lists every Société flash-hotel reward pool (admin), newest-updated first by `updatedAt`.
 * Pools are created lazily — on an offer's first reservation or its first cap write — so this
 * listing is the only way to see every offer that currently has pool state (a cap and/or
 * reservation activity), not just the ones an admin happens to know the offer ID for.
 * TODO(LOYAL-2863): replace local types with @luxuryescapes/contract-svc-lux-loyalty once the version with pool/config endpoints ships.
 */
export async function listRewardsPools(
  params: { limit?: number; offset?: number } = {},
): Promise<RewardsPoolsListResult> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) {
    qs.set('limit', String(params.limit));
  }
  if (params.offset !== undefined) {
    qs.set('offset', String(params.offset));
  }

  const search = qs.toString();
  const url = `${BASE_PATH}/rewards/pools${search ? `?${search}` : ''}`;
  const response = await request(url, { method: 'GET' });
  return response.result;
}

/**
 * Replaces the flashHotel catalog's 7 pricing/display constants (full object; changes formulaVersion).
 * TODO(LOYAL-2863): replace local types with @luxuryescapes/contract-svc-lux-loyalty once the version with pool/config endpoints ships.
 */
export async function updateRewardsCatalogConfig(
  catalogId: string,
  config: FlashHotelCatalogConfig,
): Promise<{ config: FlashHotelCatalogConfig; formulaVersion: string }> {
  const response = await request(`${BASE_PATH}/rewards/catalogs/${catalogId}/config`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify({ config }),
  });
  return response.result;
}

// ---------------------------------------------------------------------------
// Société package rewards (svc-offer)
// Unlike the rest of this file these endpoints live on svc-offer, not svc-lux-loyalty: the reward
// is stored per offer package. svc-offer's contract package does not expose them yet, so the
// types are hand-written.
// ---------------------------------------------------------------------------

const OFFERS_BASE_PATH = `${window.configs.API_HOST}/api/offers`;

/**
 * The live status of a package's Société reward.
 * - `AUTO_ELIGIBLE` / `AUTO_INELIGIBLE` — set by the pricing formula.
 * - `ADMIN_ELIGIBLE` / `ADMIN_INELIGIBLE` — set by an admin override (`eligible: true`/`false`).
 *
 * Eligibility is never decided by comparing against a single one of these values — use
 * `isSocieteRewardEligible` (true for `AUTO_ELIGIBLE` or `ADMIN_ELIGIBLE`).
 */
export type SocieteRewardStatus = 'AUTO_ELIGIBLE' | 'AUTO_INELIGIBLE' | 'ADMIN_ELIGIBLE' | 'ADMIN_INELIGIBLE';

export interface SocieteRewardOverrideMeta {
  /**
   * Acting admin's identity. Only the admin-only overrides audit listing carries it — the shared
   * offer/package GETs omit it, since those endpoints also serve vendor roles.
   */
  by?: string;
  at: string;
  reason: string;
  fields: Array<string>;
}

export interface SocieteReward {
  status: SocieteRewardStatus;
  points_base: number;
  /** Null when the package has no nightly price (an extra night can't be booked); 0 is a free extra night. */
  points_extra_night: number | null;
  is_manual_override: boolean;
  override_meta: SocieteRewardOverrideMeta | null;
  /** What the pricing formula itself last decided — always one of the two AUTO_* values, regardless of any admin override on `status`. */
  computed_status: Exclude<SocieteRewardStatus, 'ADMIN_ELIGIBLE' | 'ADMIN_INELIGIBLE'>;
  computed_points_base: number;
  computed_points_extra_night: number | null;
  /**
   * The pricing formula's reason the package was ineligible, as of its last computation. Written
   * only by the formula — never by an admin override — so this can be non-null even when `status`
   * is `ADMIN_ELIGIBLE` (the formula rejected it, an admin forced it on). Show it as formula
   * context in that case, not as the live reason.
   */
  computed_ineligible_reason: string | null;
  formula_version: string;
  computed_at: string;
  updated_at: string;
}

export interface SocieteRewardCopayPrice {
  currency_code: string;
  societe_reward_copay_base: number | null;
  societe_reward_copay_extra_night: number | null;
  // Server-computed base/extra-night copay as of the reward's last formula run (LOYAL-2857) —
  // optional since it's absent (`undefined`) on responses from before this field existed, which
  // is distinct from an explicit `null` (the field exists but this currency has no base copay).
  // Feeds the override dialog's "formula" value in the formula-vs-override margin/VPP comparison.
  societe_reward_computed_copay_base?: number | null;
  societe_reward_computed_copay_extra_night?: number | null;
}

export interface SocieteRewardCopayInput {
  currency_code: string;
  copay_base: number;
  copay_extra_night?: number | null;
}

export type SocieteRewardAdminAction =
  | {
      action: 'override';
      points_base?: number;
      points_extra_night?: number | null;
      copay?: Array<SocieteRewardCopayInput>;
      eligible?: boolean;
      reason: string;
    }
  | { action: 'clear_override' }
  | { action: 'disable' }
  | { action: 'enable' };

export interface SocieteRewardPatchResult {
  societe_reward: SocieteReward;
  prices: Array<SocieteRewardCopayPrice>;
}

export interface SocieteRewardOverrideListRow {
  offer_id_salesforce_external: string;
  offer_name: string | null;
  package_id: string;
  package_id_salesforce_external: string | null;
  package_name: string | null;
  status: SocieteRewardStatus;
  points_base: number;
  points_extra_night: number | null;
  override_meta: SocieteRewardOverrideMeta | null;
  computed_at: string;
  formula_version: string;
  prices: Array<SocieteRewardCopayPrice>;
}

/**
 * Applies an admin action (override / clear_override / disable / enable) to one package's Société reward.
 * @param offerSfId - Salesforce external offer ID
 * @param packageId - Package ID
 * @param action - Admin action to apply
 * @returns Patch result containing updated reward and copay prices
 */
export async function patchPackageSocieteReward(
  offerSfId: string,
  packageId: string,
  action: SocieteRewardAdminAction,
): Promise<SocieteRewardPatchResult> {
  const response = await request(`${OFFERS_BASE_PATH}/${offerSfId}/packages/${packageId}/societe-reward`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(action),
  });
  return response.result;
}

/**
 * One page of manually overridden packages (audit list), newest override first.
 * @param params - Pagination parameters
 * @param params.limit - Maximum number of results (default 20)
 * @param params.offset - Number of results to skip (default 0)
 * @returns Object containing result array and total count
 */
export async function listSocieteRewardOverrides({
  limit = 20,
  offset = 0,
}: { limit?: number; offset?: number } = {}): Promise<{ result: Array<SocieteRewardOverrideListRow>; total: number }> {
  const response = await request(`${OFFERS_BASE_PATH}/societe-reward/overrides?limit=${limit}&offset=${offset}`, {
    method: 'GET',
  });
  return { result: response.result, total: response.total };
}
